import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SideBar from "./DashBoard/SideBar";
import Navbar from "./DashBoard/Navbar";
import ReactMarkdown from "react-markdown";
import { API_BASE_URL } from "../config/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileCode,
  faPlus,
  faListCheck,
  faTrash,
  faUpload,
  faCheckCircle,
  faArrowLeft,
  faBookOpen,
  faCopy,
  faDownload,
  faEdit,
  faSpinner,
  faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import { Modal } from "antd";

function AddQuestions() {
  const location = useLocation();
  const navigate = useNavigate();
  const courseId = location.pathname.split("/")[2];

  const [courseInfo, setCourseInfo] = useState(null);
  const [activeTab, setActiveTab] = useState("markdown"); // "markdown" | "manual" | "existing"
  const [existingQuestions, setExistingQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);

  // Markdown Import state
  const [markdownText, setMarkdownText] = useState("");
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);

  // Manual single form state
  const [singleForm, setSingleForm] = useState({
    question: "",
    option1: "",
    option2: "",
    option3: "",
    option4: "",
    answer: ""
  });

  const sampleMarkdown = `### Question 1: What is JSX in React?
- A) Java System Extension
- B) JavaScript XML
- C) JSON eXtended
- D) JavaScript Xerox
**Answer:** B) JavaScript XML

### Question 2: What is the output of the following code snippet?
\`\`\`javascript
const numbers = [1, 2, 3];
const doubled = numbers.map(n => n * 2);
console.log(doubled);
\`\`\`
- A) [1, 2, 3]
- B) [2, 4, 6]
- C) [1, 4, 9]
- D) undefined
**Answer:** B) [2, 4, 6]

### Question 3: What command starts the standard React dev server?
- A) npm run build
- B) npm start
- C) npm compile
- D) node server.js
**Answer:** B) npm start`;

  useEffect(() => {
    if (!localStorage.getItem("adminId")) { navigate("/admin-login"); return; }
    // Fetch course details
    fetch(`${API_BASE_URL}/api/courses`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const match = data.find((c) => (c.course_id || c.id) == courseId);
          setCourseInfo(match || null);
        }
      })
      .catch((err) => console.error(err));

    // Fetch existing questions
    fetchExistingQuestions();
  }, [courseId]);

  const fetchExistingQuestions = () => {
    setLoadingQuestions(true);
    fetch(`${API_BASE_URL}/api/questions/${courseId}`)
      .then((r) => r.json())
      .then((data) => {
        setExistingQuestions(Array.isArray(data) ? data : []);
        setLoadingQuestions(false);
      })
      .catch((err) => {
        console.error(err);
        setLoadingQuestions(false);
      });
  };

  // Ultra-Robust & Flexible Markdown Quiz Parser
  const parseMarkdownQuiz = (text, autoSave = false) => {
    if (!text || !text.trim()) {
      toast.error("Please enter or upload Markdown text first.");
      return [];
    }

    // Split text into question blocks based on markdown headers or numbered questions
    const rawBlocks = text.split(/(?=\n?#{1,3}\s*(?:Question|Q|\d+)|\n?\bQuestion\s*\d*:?|\n?\bQ\d*:?|\n?\b\d+[.)]\s+[A-Z])/i).filter((b) => b.trim());
    const parsed = [];

    rawBlocks.forEach((block) => {
      const rawLines = block.split("\n");
      let headerText = "";
      let questionBodyLines = [];
      let options = [];
      let rawAnswer = "";
      let parsingState = "header"; // "header" -> "body" -> "options" -> "answer"

      for (let i = 0; i < rawLines.length; i++) {
        const line = rawLines[i];
        const trimmed = line.trim();

        // Check if line is Answer declaration
        if (/^\*{0,2}(Answer|Correct Answer|Correct|Key):\*{0,2}/i.test(trimmed)) {
          parsingState = "answer";
          rawAnswer = trimmed.replace(/^\*{0,2}(Answer|Correct Answer|Correct|Key):\*{0,2}\s*/i, "").trim();
          continue;
        }

        // Check if line is Option declaration
        const optionMatch = trimmed.match(/^[-*]?\s*([A-D\d][).:-]|Option\s*[A-D]:?)\s*(.*)$/i);
        if (optionMatch && (parsingState === "header" || parsingState === "body" || parsingState === "options")) {
          parsingState = "options";
          const optVal = optionMatch[2].trim();
          if (optVal) options.push(optVal);
          continue;
        }

        // Check header line
        if (parsingState === "header") {
          if (!trimmed) continue;
          if (/^(#{1,3}|\d+[.)]|Question\s*\d*:?|Q\d*:?)/i.test(trimmed)) {
            headerText = trimmed
              .replace(/^#{1,3}\s*/, "")
              .replace(/^Question\s*\d*:?\s*/i, "")
              .replace(/^Q\d*:?\s*/i, "")
              .replace(/^\d+[.)]\s*/, "")
              .trim();
            parsingState = "body";
            continue;
          } else {
            headerText = trimmed;
            parsingState = "body";
            continue;
          }
        }

        // If in body state (accumulate lines for multiline question text and code blocks)
        if (parsingState === "body") {
          questionBodyLines.push(line);
        }
      }

      // Construct full question text combining header and body
      const fullQuestionBody = questionBodyLines.join("\n").trim();
      const fullQuestion = headerText
        ? (fullQuestionBody ? `${headerText}\n\n${fullQuestionBody}` : headerText)
        : fullQuestionBody;

      // Match answer to option text
      let finalAnswer = rawAnswer;
      if (rawAnswer) {
        const letterMatch = rawAnswer.match(/^[([*]*([A-D])[)]].:-*]*\s*(.*)$/i);
        if (letterMatch) {
          const letter = letterMatch[1].toUpperCase();
          const letterIdx = letter.charCodeAt(0) - 65;
          const restOfAns = letterMatch[2].trim();

          if (restOfAns && options.some((opt) => opt.toLowerCase() === restOfAns.toLowerCase())) {
            const matchedOpt = options.find((opt) => opt.toLowerCase() === restOfAns.toLowerCase());
            finalAnswer = matchedOpt || restOfAns;
          } else if (options[letterIdx]) {
            finalAnswer = options[letterIdx];
          } else if (restOfAns) {
            finalAnswer = restOfAns;
          }
        }
      }

      if (fullQuestion && options.length >= 2) {
        parsed.push({
          question: fullQuestion,
          option1: options[0] || "",
          option2: options[1] || "",
          option3: options[2] || "",
          option4: options[3] || "",
          answer: finalAnswer || options[0] || ""
        });
      }
    });

    if (parsed.length === 0) {
      toast.error("Could not parse any valid questions. Please check the Markdown format guide.");
    } else {
      setParsedQuestions(parsed);
      if (!autoSave) {
        toast.success(`Successfully parsed ${parsed.length} questions! Review below or click Save.`);
      }
    }
    return parsed;
  };

  // Upload .md file
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target.result;
        setMarkdownText(content);
        parseMarkdownQuiz(content);
      };
      reader.readAsText(file);
    }
  };

  // Submit Bulk Questions
  const handleBulkSubmit = async (questionsToSubmit = null) => {
    let targetQuestions = questionsToSubmit || parsedQuestions;

    if (!Array.isArray(targetQuestions) || targetQuestions.length === 0) {
      if (markdownText && markdownText.trim()) {
        targetQuestions = parseMarkdownQuiz(markdownText, true);
      }
    }

    if (!Array.isArray(targetQuestions) || targetQuestions.length === 0) {
      toast.error("No valid questions found to save. Please enter or upload Markdown text first.");
      return;
    }

    setIsSubmittingBulk(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/questions/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          courseId: Number(courseId),
          questions: targetQuestions
        })
      });

      if (res.ok) {
        toast.success(`Successfully saved ${targetQuestions.length} quiz questions to course!`);
        setParsedQuestions([]);
        setMarkdownText("");
        fetchExistingQuestions();
        setActiveTab("existing");
      } else {
        toast.error("Failed to save questions to database.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error connecting to server.");
    } finally {
      setIsSubmittingBulk(false);
    }
  };

  // Submit Single Manual Question
  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!singleForm.question || !singleForm.option1 || !singleForm.option2 || !singleForm.answer) {
      toast.error("Please fill in question, options, and correct answer.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          courseId: Number(courseId),
          ...singleForm
        })
      });

      if (res.ok) {
        toast.success("Single question added successfully!");
        setSingleForm({ question: "", option1: "", option2: "", option3: "", option4: "", answer: "" });
        fetchExistingQuestions();
        setActiveTab("existing");
      } else {
        toast.error("Could not add question.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error connecting to server.");
    }
  };

  // Delete single question
  const handleDeleteQuestion = async (qId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/questions/${qId}`, { method: "DELETE" });
      if (res.ok || res.status === 204) {
        toast.success("Question removed.");
        fetchExistingQuestions();
      } else {
        toast.error("Could not delete question.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error connecting to server.");
    }
  };

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <SideBar current={"courses"} />
      <section id="content">
        <Navbar />
        <main style={{ padding: "28px", maxWidth: "1050px", margin: "0 auto" }}>

          {/* Top Header */}
          <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
            <div>
              <button
                onClick={() => navigate("/Dcourses")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#4f46e5",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "8px",
                  padding: 0
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Courses
              </button>
              <h1 style={{ color: "#0f172a", fontFamily: "'Outfit', sans-serif", fontSize: "1.8rem", fontWeight: 800, margin: 0 }}>
                Manage Quiz: {courseInfo?.course_name || courseInfo?.courseName || `Course #${courseId}`}
              </h1>
              <p style={{ color: "#64748b", margin: "4px 0 0", fontSize: "0.9rem" }}>
                Import complete quizzes via Markdown files or add custom single questions
              </p>
            </div>

            <button
              onClick={() => setShowGuideModal(true)}
              style={{
                background: "#e0e7ff",
                color: "#4f46e5",
                border: "none",
                borderRadius: "10px",
                padding: "10px 18px",
                fontWeight: 700,
                fontSize: "0.88rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              <FontAwesomeIcon icon={faInfoCircle} /> View Markdown Format Guide
            </button>
          </div>

          {/* Tab Selection Controls */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "24px", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px" }}>
            <button
              onClick={() => setActiveTab("markdown")}
              style={{
                padding: "10px 20px",
                borderRadius: "10px",
                border: "none",
                background: activeTab === "markdown" ? "#4f46e5" : "transparent",
                color: activeTab === "markdown" ? "#fff" : "#64748b",
                fontWeight: 700,
                fontSize: "0.9rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              <FontAwesomeIcon icon={faFileCode} /> Markdown Quiz Importer
            </button>

            <button
              onClick={() => setActiveTab("manual")}
              style={{
                padding: "10px 20px",
                borderRadius: "10px",
                border: "none",
                background: activeTab === "manual" ? "#4f46e5" : "transparent",
                color: activeTab === "manual" ? "#fff" : "#64748b",
                fontWeight: 700,
                fontSize: "0.9rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Single Question Form
            </button>

            <button
              onClick={() => setActiveTab("existing")}
              style={{
                padding: "10px 20px",
                borderRadius: "10px",
                border: "none",
                background: activeTab === "existing" ? "#4f46e5" : "transparent",
                color: activeTab === "existing" ? "#fff" : "#64748b",
                fontWeight: 700,
                fontSize: "0.9rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              <FontAwesomeIcon icon={faListCheck} /> Saved Course Questions ({existingQuestions.length})
            </button>
          </div>

          {/* TAB 1: MARKDOWN IMPORT */}
          {activeTab === "markdown" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

              {/* Upload & Paste Card */}
              <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e2e8f0", padding: "28px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
                  <h3 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: "1.1rem" }}>
                    Paste Markdown or Upload .md File
                  </h3>

                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={() => {
                        setMarkdownText(sampleMarkdown);
                        parseMarkdownQuiz(sampleMarkdown);
                      }}
                      style={{
                        background: "#f1f5f9",
                        color: "#334155",
                        border: "none",
                        padding: "8px 14px",
                        borderRadius: "8px",
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        cursor: "pointer"
                      }}
                    >
                      Load Sample Template
                    </button>

                    <label
                      style={{
                        background: "#06b6d4",
                        color: "#fff",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                    >
                      <FontAwesomeIcon icon={faUpload} /> Upload .md File
                      <input type="file" accept=".md,.txt" onChange={handleFileUpload} style={{ display: "none" }} />
                    </label>
                  </div>
                </div>

                <textarea
                  rows={10}
                  placeholder={`Paste Markdown quiz format here...\n\nExample:\n### Question 1: What is JSX?\n- A) Java Extension\n- B) JavaScript XML\n- C) JSON Syntax\n- D) None\n**Answer:** B) JavaScript XML`}
                  value={markdownText}
                  onChange={(e) => setMarkdownText(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "14px",
                    borderRadius: "12px",
                    border: "1.5px solid #cbd5e1",
                    fontFamily: "monospace",
                    fontSize: "0.88rem",
                    lineHeight: 1.5,
                    outline: "none",
                    marginBottom: "16px"
                  }}
                />

                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  <button
                    onClick={() => {
                      const questions = parseMarkdownQuiz(markdownText, true);
                      if (questions && questions.length > 0) {
                        handleBulkSubmit(questions);
                      }
                    }}
                    disabled={isSubmittingBulk}
                    style={{
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "#fff",
                      border: "none",
                      padding: "12px 24px",
                      borderRadius: "10px",
                      fontWeight: 800,
                      fontSize: "0.9rem",
                      cursor: isSubmittingBulk ? "wait" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      boxShadow: "0 4px 14px rgba(16,185,129,0.3)"
                    }}
                  >
                    <FontAwesomeIcon icon={faCheckCircle} />
                    {isSubmittingBulk ? "Saving Quiz..." : "Parse & Save Directly to Course"}
                  </button>

                  <button
                    onClick={() => parseMarkdownQuiz(markdownText)}
                    style={{
                      background: "#f1f5f9",
                      color: "#334155",
                      border: "1.5px solid #cbd5e1",
                      padding: "12px 24px",
                      borderRadius: "10px",
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      cursor: "pointer"
                    }}
                  >
                    Parse & Preview Questions First
                  </button>
                </div>
              </div>

              {/* Parsed Questions Review & Edit Section */}
              {parsedQuestions.length > 0 && (
                <div style={{ background: "#fff", borderRadius: "20px", border: "1.5px solid #4f46e5", padding: "28px", boxShadow: "0 10px 25px rgba(79,70,229,0.08)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
                    <div>
                      <h3 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: "1.2rem" }}>
                        Extracted Questions ({parsedQuestions.length})
                      </h3>
                      <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem" }}>
                        Review, edit choices or answers below, then click Submit Quiz
                      </p>
                    </div>

                    <button
                      onClick={handleBulkSubmit}
                      disabled={isSubmittingBulk}
                      style={{
                        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        color: "#fff",
                        border: "none",
                        padding: "12px 28px",
                        borderRadius: "10px",
                        fontWeight: 800,
                        fontSize: "0.95rem",
                        cursor: "pointer",
                        boxShadow: "0 4px 14px rgba(16,185,129,0.3)"
                      }}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "8px" }} />
                      {isSubmittingBulk ? "Saving Quiz..." : "Submit All Extracted Questions"}
                    </button>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                    {parsedQuestions.map((q, idx) => (
                      <div key={idx} style={{ background: "#f8fafc", borderRadius: "14px", border: "1px solid #e2e8f0", padding: "20px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                          <span style={{ fontWeight: 800, color: "#4f46e5", fontSize: "0.9rem" }}>
                            Question #{idx + 1}
                          </span>
                          <button
                            onClick={() => setParsedQuestions(parsedQuestions.filter((_, i) => i !== idx))}
                            style={{ background: "#fee2e2", color: "#ef4444", border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "0.8rem", cursor: "pointer" }}
                          >
                            <FontAwesomeIcon icon={faTrash} /> Remove
                          </button>
                        </div>

                        <textarea
                          rows={3}
                          value={q.question}
                          onChange={(e) => {
                            const copy = [...parsedQuestions];
                            copy[idx].question = e.target.value;
                            setParsedQuestions(copy);
                          }}
                          style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontWeight: 600, fontFamily: "monospace", fontSize: "0.85rem", marginBottom: "12px" }}
                        />

                        <div style={{ background: "#fff", padding: "12px", borderRadius: "8px", border: "1px dashed #cbd5e1", marginBottom: "12px", fontSize: "0.88rem" }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", display: "block", marginBottom: "4px" }}>Live Markdown Preview:</span>
                          <ReactMarkdown>{q.question}</ReactMarkdown>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                          {["option1", "option2", "option3", "option4"].map((optKey, oIdx) => (
                            <div key={optKey}>
                              <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#64748b" }}>Option {oIdx + 1}</label>
                              <input
                                type="text"
                                value={q[optKey]}
                                onChange={(e) => {
                                  const copy = [...parsedQuestions];
                                  copy[idx][optKey] = e.target.value;
                                  setParsedQuestions(copy);
                                }}
                                style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "0.88rem" }}
                              />
                            </div>
                          ))}
                        </div>

                        <div>
                          <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#059669" }}>Correct Answer</label>
                          <input
                            type="text"
                            value={q.answer}
                            onChange={(e) => {
                              const copy = [...parsedQuestions];
                              copy[idx].answer = e.target.value;
                              setParsedQuestions(copy);
                            }}
                            style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: "1.5px solid #10b981", fontWeight: 700, color: "#065f46" }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* TAB 2: MANUAL SINGLE FORM */}
          {activeTab === "manual" && (
            <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e2e8f0", padding: "32px", maxWidth: "650px" }}>
              <h3 style={{ margin: "0 0 20px", fontWeight: 800, color: "#0f172a", fontSize: "1.15rem" }}>
                Add Custom Single Question
              </h3>
              <form onSubmit={handleSingleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>Question Text (Supports Markdown)</label>
                  <textarea
                    rows={3}
                    value={singleForm.question}
                    onChange={(e) => setSingleForm({ ...singleForm, question: e.target.value })}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontFamily: "monospace", fontSize: "0.88rem" }}
                    required
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <div>
                    <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>Option 1</label>
                    <input
                      type="text"
                      value={singleForm.option1}
                      onChange={(e) => setSingleForm({ ...singleForm, option1: e.target.value })}
                      style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1" }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>Option 2</label>
                    <input
                      type="text"
                      value={singleForm.option2}
                      onChange={(e) => setSingleForm({ ...singleForm, option2: e.target.value })}
                      style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1" }}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <div>
                    <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>Option 3</label>
                    <input
                      type="text"
                      value={singleForm.option3}
                      onChange={(e) => setSingleForm({ ...singleForm, option3: e.target.value })}
                      style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>Option 4</label>
                    <input
                      type="text"
                      value={singleForm.option4}
                      onChange={(e) => setSingleForm({ ...singleForm, option4: e.target.value })}
                      style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1" }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#059669", marginBottom: "6px", display: "block" }}>Correct Answer Text</label>
                  <input
                    type="text"
                    value={singleForm.answer}
                    onChange={(e) => setSingleForm({ ...singleForm, answer: e.target.value })}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #10b981", fontWeight: 700 }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  style={{
                    background: "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)",
                    color: "#fff",
                    border: "none",
                    padding: "12px",
                    borderRadius: "10px",
                    fontWeight: 700,
                    marginTop: "8px",
                    cursor: "pointer"
                  }}
                >
                  Add Question
                </button>
              </form>
            </div>
          )}

          {/* TAB 3: EXISTING SAVED QUESTIONS */}
          {activeTab === "existing" && (
            <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e2e8f0", padding: "28px" }}>
              <h3 style={{ margin: "0 0 20px", fontWeight: 800, color: "#0f172a", fontSize: "1.15rem" }}>
                Saved Course Questions ({existingQuestions.length})
              </h3>

              {loadingQuestions ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.8rem", color: "#4f46e5", marginBottom: "10px" }} />
                  <p>Loading saved questions...</p>
                </div>
              ) : existingQuestions.length === 0 ? (
                <p style={{ color: "#94a3b8", textAlign: "center" }}>No questions added for this course yet.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {existingQuestions.map((q, idx) => (
                    <div key={q.id || idx} style={{ background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "16px 20px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
                        <div style={{ margin: 0, fontWeight: 700, color: "#0f172a", fontSize: "0.95rem" }}>
                          <span style={{ color: "#4f46e5", fontWeight: 800, marginRight: "8px" }}>Q{idx + 1}:</span>
                          <div style={{ display: "inline-block", verticalAlign: "top" }}>
                            <ReactMarkdown>{q.question}</ReactMarkdown>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteQuestion(q.id)}
                          style={{ background: "#fee2e2", color: "#ef4444", border: "none", borderRadius: "6px", padding: "6px 10px", cursor: "pointer", fontSize: "0.8rem" }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "8px", fontSize: "0.85rem", color: "#475569" }}>
                        <div>A) {q.option1}</div>
                        <div>B) {q.option2}</div>
                        <div>C) {q.option3}</div>
                        <div>D) {q.option4}</div>
                      </div>

                      <div style={{ marginTop: "10px", fontSize: "0.83rem", fontWeight: 700, color: "#059669" }}>
                        ✓ Correct Answer: {q.answer}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </main>
      </section>

      {/* Format Guide Modal */}
      <Modal
        title="Markdown Quiz Import Format Specification"
        open={showGuideModal}
        onCancel={() => setShowGuideModal(false)}
        footer={null}
        width={650}
      >
        <div style={{ fontSize: "0.9rem", color: "#334155", lineHeight: 1.6 }}>
          <p>Each question block in your <code>.md</code> file should be formatted like this:</p>
          <pre
            style={{
              background: "#0f172a",
              color: "#38bdf8",
              padding: "16px",
              borderRadius: "10px",
              fontSize: "0.85rem",
              lineHeight: 1.5,
              overflowX: "auto"
            }}
          >
            {sampleMarkdown}
          </pre>
          <ul style={{ marginTop: "14px", paddingLeft: "20px" }}>
            <li>Start questions with <code>### Question X: [Question text]</code> or <code>### Q: [Text]</code>.</li>
            <li>List 4 choices starting with <code>- A)</code>, <code>- B)</code>, <code>- C)</code>, <code>- D)</code>.</li>
            <li>Specify correct answer on a line like <code>**Answer:** B) [Answer text]</code>.</li>
          </ul>
        </div>
      </Modal>
    </div>
  );
}

export default AddQuestions;